import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronsUpDown, Check, Plus, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';
import { adminApi } from '@/lib/adminApi';
import { formatApiError } from '@/lib/api';
import { toast } from 'sonner';

/**
 * Sprint A.1.1 — Searchable, creatable combobox for shared catalog fields
 * (Professional Area, Evaluation Type, Level, Language, Category).
 *
 * - `field`    : (string) the catalog field name — required to persist new values.
 * - `options`  : [{code, label_en, label_es}] — pulled from /admin/library/vocab.
 * - `value`    : current code (existing or freshly created).
 * - `onChange` : (newCode: string|null) => void
 * - `onVocabExtended`: optional callback(newEntry) — parent should re-fetch
 *                vocab or merge the new entry into local state.
 * - "+ Create" footer POSTs to /admin/library/vocab/{field}, then selects
 *   the returned code so the value is shared across every evaluation.
 */
export function VocabCombobox({
  field,
  value,
  onChange,
  options = [],
  placeholder,
  emptyText,
  disabled,
  testId,
  onVocabExtended,
}) {
  const { t, i18n } = useTranslation();
  const isEN = (i18n.language || '').toLowerCase().startsWith('en');
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState(false);

  const labelOf = (entry) => (isEN ? entry.label_en : entry.label_es) || entry.label_en || entry.code;
  const display = useMemo(() => {
    const hit = options.find((o) => o.code === value);
    if (hit) return labelOf(hit);
    return value || '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, options, isEN]);

  const trimmed = (query || '').trim();
  const knownCodes = useMemo(() => new Set(options.map((o) => o.code)), [options]);
  const knownLabelsLc = useMemo(
    () => new Set(options.flatMap((o) => [
      (o.label_en || '').toLowerCase().trim(),
      (o.label_es || '').toLowerCase().trim(),
    ]).filter(Boolean)),
    [options],
  );
  const canCreate =
    trimmed.length > 0 &&
    !knownCodes.has(trimmed) &&
    !knownLabelsLc.has(trimmed.toLowerCase()) &&
    !!field;

  const close = () => { setOpen(false); setQuery(''); };

  const select = (code) => {
    onChange(code || null);
    close();
  };

  const createNew = async () => {
    if (!field || !trimmed || creating) return;
    setCreating(true);
    try {
      const labels = isEN
        ? { label_en: trimmed, label_es: trimmed }
        : { label_en: trimmed, label_es: trimmed };
      const res = await adminApi.createVocabValue(field, labels);
      const entry = res?.entry || {};
      if (res?.duplicate) {
        toast.info(t('library.vocab.alreadyExists', { defaultValue: 'Already in catalog — selecting existing entry.' }));
      } else {
        toast.success(t('library.vocab.created', { defaultValue: 'Added to shared catalog' }));
      }
      onVocabExtended?.(entry);
      select(entry.code || trimmed);
    } catch (e) {
      toast.error(t('library.vocab.createFailed', { defaultValue: 'Could not create' }), {
        description: formatApiError(e),
      });
    } finally {
      setCreating(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setQuery(''); }}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={testId}
          className={cn(
            'w-full justify-between font-normal',
            !display && 'text-muted-foreground',
          )}
        >
          <span className="truncate">{display || (placeholder || t('common.choose', { defaultValue: 'Select…' }))}</span>
          <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0 ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter>
          <CommandInput
            value={query}
            onValueChange={setQuery}
            placeholder={placeholder || t('common.search', { defaultValue: 'Search…' })}
            data-testid={testId ? `${testId}-input` : undefined}
          />
          <CommandList>
            <CommandEmpty>{emptyText || t('common.noResults', { defaultValue: 'No matches' })}</CommandEmpty>
            <CommandGroup>
              {value && !knownCodes.has(value) && (
                <CommandItem
                  value={`__custom:${value}`}
                  onSelect={() => select(value)}
                  data-testid={testId ? `${testId}-current-custom` : undefined}
                >
                  <Check className="mr-2 h-4 w-4 opacity-100" />
                  <span className="italic">{value}</span>
                  <span className="ml-auto text-xs uppercase tracking-wider text-muted-foreground">
                    {t('library.vocab.custom', { defaultValue: 'Custom' })}
                  </span>
                </CommandItem>
              )}
              {options.map((o) => (
                <CommandItem
                  key={o.code}
                  value={`${o.code} ${o.label_en} ${o.label_es}`}
                  onSelect={() => select(o.code)}
                  data-testid={testId ? `${testId}-opt-${o.code}` : undefined}
                >
                  <Check className={cn('mr-2 h-4 w-4', value === o.code ? 'opacity-100' : 'opacity-0')} />
                  <span>{labelOf(o)}</span>
                </CommandItem>
              ))}
              {canCreate && (
                <CommandItem
                  value={`__create:${trimmed}`}
                  onSelect={createNew}
                  data-testid={testId ? `${testId}-create` : undefined}
                  className="text-primary"
                  disabled={creating}
                >
                  {creating
                    ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    : <Plus className="mr-2 h-4 w-4" />}
                  {t('library.vocab.createNew', { defaultValue: 'Create' })}: <span className="font-medium ml-1">“{trimmed}”</span>
                </CommandItem>
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default VocabCombobox;
